import React, { useMemo } from "react";
import EChart from "./EChart";
import { ECHART_TOOLTIP, ECHART_TOOLBOX, ECHART_STATUS_COLORS, ECHART_PALETTE, ECHART_COLORS } from "../../Utilities/echartsTheme";
import { formatNumber, formatRawNumber } from "../../Utilities/numbers";

const EChartsStatusCombo = ({
  data,
  labelKey = "status",
  countKey = "source_count",
  valueKey = "amount",
  colorMap = ECHART_STATUS_COLORS,
  countLabel = "Orders",
  valueLabel = "Value",
  valuePrefix = "Rs ",
}) => {
  const option = useMemo(() => {
    const items = (data || []).slice().sort((a, b) => Number(b[valueKey]) - Number(a[valueKey]));
    const labels = items.map((d) => d[labelKey]);
    const counts = items.map((d) => Number(d[countKey]) || 0);
    const amounts = items.map((d) => Number(d[valueKey]) || 0);
    const colors = items.map((d, i) => colorMap[d[labelKey]] || ECHART_PALETTE[i % ECHART_PALETTE.length]);

    return {
      tooltip: {
        ...ECHART_TOOLTIP,
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const name = params[0].name;
          const count = params.find((p) => p.seriesName === "Count")?.value || 0;
          const value = params.find((p) => p.seriesName === "Value")?.value || 0;
          return `<div style="font-weight:600;margin-bottom:4px">${name}</div>
            <div>${countLabel}: <strong>${formatRawNumber(count)}</strong></div>
            <div>${valueLabel}: <strong>${valuePrefix}${formatRawNumber(value)}</strong></div>`;
        },
      },
      legend: {
        top: 0,
        right: 10,
        textStyle: { fontSize: 12, color: "#64748B" },
        itemWidth: 14,
        itemHeight: 10,
      },
      toolbox: ECHART_TOOLBOX,
      grid: { left: "3%", right: "8%", bottom: "3%", top: "12%", containLabel: true },
      xAxis: [
        {
          type: "value",
          name: countLabel,
          nameTextStyle: { color: ECHART_COLORS.primaryLight, fontSize: 11 },
          position: "bottom",
          axisLabel: { formatter: (v) => formatNumber(v), color: ECHART_COLORS.primaryLight },
          splitLine: { lineStyle: { color: "#F1F5F9" } },
        },
        {
          type: "value",
          name: valueLabel,
          nameTextStyle: { color: ECHART_COLORS.accent, fontSize: 11 },
          position: "top",
          axisLabel: { formatter: (v) => formatNumber(v), color: ECHART_COLORS.accent },
          splitLine: { show: false },
        },
      ],
      yAxis: {
        type: "category",
        data: labels,
        axisLabel: {
          width: 120,
          overflow: "truncate",
          fontSize: 11,
        },
      },
      series: [
        {
          name: "Count",
          type: "bar",
          xAxisIndex: 0,
          data: counts.map((v, i) => ({
            value: v,
            itemStyle: { color: colors[i], borderRadius: [0, 4, 4, 0] },
          })),
          barMaxWidth: 20,
          label: {
            show: true,
            position: "right",
            formatter: (p) => formatNumber(p.value),
            fontSize: 11,
            color: "#64748B",
          },
          animationDelay: (idx) => idx * 50,
        },
        {
          name: "Value",
          type: "bar",
          xAxisIndex: 1,
          data: amounts.map((v, i) => ({
            value: v,
            itemStyle: { color: colors[i] + "40", borderRadius: [0, 4, 4, 0] },
          })),
          barMaxWidth: 20,
          label: {
            show: true,
            position: "right",
            formatter: (p) => `${valuePrefix}${formatNumber(p.value)}`,
            fontSize: 11,
            color: "#64748B",
          },
          animationDelay: (idx) => idx * 50 + 100,
        },
      ],
    };
  }, [data, labelKey, countKey, valueKey, colorMap, countLabel, valueLabel, valuePrefix]);

  return <EChart option={option} />;
};

export default EChartsStatusCombo;
